/**
 * Shared, delayed and budgeted solo requests (RHistory, GC5 fix round).
 *
 * A RIC that a multi-RIC history answer left empty is asked again alone
 * (grids.ts rhistoryLaid). On 2026-10-24 those RICs were the ones LSEG had
 * answered `429 Too Many Requests` inside a 1,150-call recalculation, so the
 * re-ask must not add to the storm:
 * - every cell holding the same RIC, window and FIDs shares ONE re-ask
 *   (`shareInFlight`): five cells on a swallowed RIC cost one solo run;
 * - the re-ask starts after REASK.delayMs plus up to REASK.jitterMs, out of
 *   the 429 window, spread out;
 * - at most REASK.maxRics RICs are re-asked per REASK.windowMs per runtime;
 *   beyond that the cell keeps its no-data result ("#N/A"), as before GC5.
 * The solo requests also run in the batcher's "solo" lane (2 of the 4 slots).
 */

export interface ReaskConfig {
  delayMs: number;
  jitterMs: number;
  maxRics: number;
  windowMs: number;
}

const DEFAULTS: ReaskConfig = { delayMs: 1500, jitterMs: 500, maxRics: 100, windowMs: 60_000 };

export const REASK: ReaskConfig = { ...DEFAULTS };

/** Override the re-ask settings (tests); null restores the defaults. */
export function configureReask(c: Partial<ReaskConfig> | null): void {
  Object.assign(REASK, DEFAULTS, c ?? {});
}

const inFlight = new Map<string, Promise<unknown>>();
/** When each re-ask of the current window started (ms). */
let started: number[] = [];

/** Forget shared requests and the budget (tests). */
export function resetReasks(): void {
  inFlight.clear();
  started = [];
}

/** Run `fn` once for every concurrent caller with the same `key`; forgotten once settled. */
export function shareInFlight<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const running = inFlight.get(key);
  if (running) return running as Promise<T>;
  const forget = () => {
    if (inFlight.get(key) === p) inFlight.delete(key);
  };
  const p: Promise<T> = fn().then(
    (v) => (forget(), v),
    (e) => {
      forget();
      throw e;
    }
  );
  inFlight.set(key, p);
  return p;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * A solo re-ask of one RIC: shared by `key`, started after the delay and
 * jitter, and counted against the budget once per shared run. Resolves null
 * when the budget is spent (the caller keeps its first result).
 */
export function sharedReask<T>(key: string, fn: () => Promise<T>): Promise<T | null> {
  const running = inFlight.get(`reask|${key}`);
  if (running) return running as Promise<T | null>;
  const now = Date.now();
  started = started.filter((t) => now - t < REASK.windowMs);
  if (started.length >= REASK.maxRics) return Promise.resolve(null);
  started.push(now);
  return shareInFlight<T | null>(`reask|${key}`, async () => {
    await sleep(REASK.delayMs + Math.random() * REASK.jitterMs);
    return fn();
  });
}
